/**
 * RP4 — Internal helpers for `SceneBulkLoad.ts`.
 *
 * Per-pass sub-functions that build buckets / fills / texts / fit
 * camera. Hoisted out so the orchestrator's `runSceneBulkLoad` driver
 * stays small enough to read top-to-bottom.
 *
 * No behavior changes — pure code move from earlier inline draft.
 */

import * as THREE from "three";
import {
  LineSegments2,
} from "../rendering/thickLines";
import { acquireLineSegmentsGeometry } from "./meshPool";

import type { SavedView, TextStyle } from "@dxf-ai-editor/drawing-model";
import type { RenderableEntity } from "../types";
import { lodBucketForEntity, isAlwaysLiftedStubType } from "../lod";
import { BatchedLayer, type MeshKeyId } from "../batchedLayer";
import {
  lineStratumRenderOrder,
  SINGLE_DRAW_ORDER_STRATUM,
  type DrawOrderStrata,
} from "../drawOrderStrata";
import {
  type LinetypeResolutionMap,
  type LayerLinetypeMap,
} from "../rendering/linetypeResolve";
import { buildBucketLineMaterial } from "../rendering/lineMaterialDashSetup";
import {
  buildPerSegmentLtScales,
  buildPerSegmentSlotIndices,
} from "../rendering/perSegmentLtScale";
import type { LinetypeSysvars } from "../rendering/linetypePatterns";
import {
  SelectionStateTexture,
  detectSelectionStateCompat,
} from "../rendering/selectionStateTexture";
import { patchMaterialWithSelectionState } from "../rendering/stateAwareMaterial";
import { resolveFont, MissingFontError } from "../text/fontRegistry";

import type { BucketState, EntityRender } from "./SceneRefsTypes";
import { applyEntityRenderVisibility } from "./entityRenderVisibility";
import {
  registerPendingTextRun,
  releasePendingTextRun,
} from "./pendingTextRetry";
import {
  attachArrowDecoratorMeshes,
  attachFillMesh,
  recordEntityRenderLayers,
  upsertEntityRender,
} from "./entityRenderAttach";
import {
  fillModePaintsSolid,
  polylineDeclaresBandWidth,
} from "../adapters/geom/polylineDrawnBand";

import {
  buildWidePolylineFillMesh,
  widePolylineFillRenderOrder,
} from "./widePolylineFillMesh";

// Dep-graph seam (ПРИОРИТЕТ #2): `upsertEntityRender` moved to
// `./entityRenderAttach.ts` with the direct-mesh attach helpers; re-exported
// here so every existing import path (`FrameLoops`, `SelectionTextureSync`,
// tests) resolves unchanged.
export { upsertEntityRender };

/** Shared empty hidden-layer set — avoids a per-call allocation when a caller
 *  passes no `hiddenLayersRef` (unit tests / hidden-layer-agnostic paths). */
const EMPTY_HIDDEN_LAYERS: ReadonlySet<string> = new Set<string>();

/**
 * S9b (Codex P2 — "Mirror selection slots to MLEADER block children" +
 * round-7 P3 — "Record satellite slots without requiring a parent slot"):
 * fan each MLEADER parent's block-content child texture slots up onto the
 * parent's `satelliteSlots`, so selecting / hovering the (single pickable)
 * MLEADER lights up its embedded block graphics too, not just the leader-line
 * slot (`applySelectionToTextures` reads `satelliteSlots`). The children occupy
 * batched-line slots under their OWN synthetic ids but are absent from the
 * spatial / selection model.
 *
 * The parent render is UPSERTED (not required to pre-exist): a content-only
 * block callout (`leaderType=0`) emits NO parent line/decorator slot, so no
 * `EntityRender` exists for it — the children are then the only entries with
 * texture slots. The pre-fix `if (!parentRender) continue` skipped such a parent,
 * leaving the block content non-highlightable; upserting keeps content-only
 * callouts selectable like leadered ones. `applySelectionToTextures` fans out
 * `satelliteSlots` independently of the parent's own `textureSlot`, so a parent
 * with satellites but no own slot still lights its whole callout.
 */
export function assignMultileaderSatelliteSlots(
  entityRenders: Map<string, EntityRender>,
  multileaderBlockChildren: ReadonlyMap<string, ReadonlyArray<{ id: string }>>,
): void {
  for (const [parentId, children] of multileaderBlockChildren) {
    const slots: NonNullable<EntityRender["textureSlot"]>[] = [];
    for (const child of children) {
      const childSlot = entityRenders.get(child.id)?.textureSlot;
      if (childSlot) slots.push(childSlot);
    }
    if (slots.length === 0) continue;
    upsertEntityRender(entityRenders, parentId).satelliteSlots = slots;
  }
}

import {
  buildArrayInstancedMeshes,
  buildHatchFillMesh,
  buildWipeoutFillMesh,
} from "./sceneMeshFactories";
import {
  buildTextEntityMesh,
  buildShxTextEntityMesh,
  buildTextPlaceholderMesh,
} from "./sceneTextMeshFactories";
import {
  buildDimensionArrowMeshes,
  synthesizeDimensionTextRenderables,
} from "./sceneDimensionMeshFactories";
import {
  buildMultileaderArrowMeshes,
  synthesizeMultileaderTextRenderables,
} from "./sceneMultileaderMeshFactories";
import { resolveDispatchFontName } from "./scenePlaceholder";

/**
 * Per-bucket space classification used to tag each `LineSegments2`
 * mesh with the model/paper-space it belongs to.
 *
 * - `"model"` — every entity in the bucket lives in model space.
 * - `"paper"` — every entity in the bucket lives in a paper-space
 *   layout (any non-"model" spaceId).
 *
 * # Why there is no `"mixed"` any more (Codex round 15 P1)
 *
 * There used to be a third class for a bucket holding BOTH model and paper
 * entities — reachable because `batchedLayer.ts MeshKey` had no space
 * component, so a model line and a paper line agreeing on layer × colour ×
 * linetype (layer "0", BYLAYER, Continuous — the commonest shape there is)
 * landed in ONE mesh. That class had no safe handling: the bare-sheet pre-hide
 * in `rendering/viewportTraversalCaches.ts` could not hide it without hiding
 * the paper half, so it KEPT it and model geometry painted on the bare sheet
 * outside every viewport scissor rect.
 *
 * `MeshKey.space` now partitions bucket identity by space, so a bucket holds
 * exactly one space by construction and the class is a total function of it.
 * The class was REMOVED rather than left unreachable: a dead branch inside the
 * traversal cache is a trap for the next reader, and a conservative-keep
 * branch is indistinguishable from the defect it used to cause.
 *
 * An UNTAGGED mesh (`userData.spaceId` absent — a foreign object carrying a
 * `keyId`) is a different case and still falls back to the layer heuristic in
 * `viewportTraversalCaches.ts`; it is not spelled by this union.
 */
export type BucketSpaceClass = "model" | "paper";

/**
 * The ONE mapping from a `spaceId` to its `BucketSpaceClass`: `"model"` for
 * model space, `"paper"` for any layout block name. Shared by the bulk-load
 * classifier (`SceneBulkLoad.ts`) and {@link deriveBucketSpaceClass} so the
 * two cannot drift.
 */
export function bucketSpaceClassOf(spaceId: string | undefined): BucketSpaceClass {
  return spaceId !== undefined && spaceId !== "model" ? "paper" : "model";
}

/**
 * FIX-1 (Codex P2) — derive a bucket's `BucketSpaceClass` from the
 * `spaceId`s of the renderables it holds, EXACTLY as the bulk-load path
 * computes it in `runSceneBulkLoad` (SceneBulkLoad.ts — "classify every
 * bucket by the space of the entities it holds").
 *
 * The FrameLoops viewport-cull relift path calls this to classify a NEW
 * bucket it materialises on demand, so the mesh's `userData.spaceId`
 * matches what a full bulk-load would have stamped. The layout
 * orchestrator's pre-hide treats `userData.spaceId` as authoritative, so
 * a hardcoded `"model"` would make a PAPER-space line (whose linetype
 * bucket is first materialised by relift) get misclassified and pre-hidden
 * on the bare sheet until the next full bulk-load.
 *
 * Codex round 15 P1 — every id in `spaceIds` belongs to the SAME bucket key,
 * and `MeshKey.space` makes the space part of that key, so the set is a
 * singleton by construction and the FIRST member decides. It is still written
 * as a loop over the set the caller already builds (rather than an index
 * lookup) so a caller that hands over an empty set — no renderable landed in
 * the bucket — still gets the `"model"` fallback `runSceneBulkLoad` uses.
 */
export function deriveBucketSpaceClass(
  spaceIds: Iterable<string>,
): BucketSpaceClass {
  for (const spaceId of spaceIds) return bucketSpaceClassOf(spaceId);
  return "model";
}

/**
 * Cache-direct LOD pre-classify partition (Codex P3, extracted from the
 * inline loop in `runSceneBulkLoad`).
 *
 * Splits metadata-stubs into the set that should be LIFTED into full
 * `RenderableEntity`s and a count of those DROPPED as sub-pixel `"skip"`
 * at the initial fit-to-extents `fitZoom`. Stubs already carry per-entity
 * bounds (`metadataToSpatialStub`), so the on-screen pixel size is
 * computed without lifting the full entity.
 *
 * TEXT / MTEXT exemption (the reported-bug fix): text entities have a
 * SMALL world bbox, so at the tiny `fitZoom` of a large drawing they all
 * bucket as `"skip"` by the bbox-diagonal rule and would be dropped. The
 * generic per-frame viewport-cull re-lift (`FrameLoops`) does NOT recover
 * them: it only feeds BatchedLayer *line* geometry, and `flattenForBucket`
 * returns `[]` for text/mtext, so re-adding a text id builds no glyphs;
 * text meshes are built ONLY by `emitTextMeshes` at bulk-load, which the
 * re-lift never re-invokes. A dropped text stub therefore never reaches
 * `emitTextMeshes` for the WHOLE session — labels stay invisible even
 * after zooming in. So text-like stubs are ALWAYS liftable and are NOT
 * counted as dropped; the LOD lift-drop no longer hides them. (The
 * separate PR-V5 `shouldSkipTextRun` height gate still applies at emit;
 * on the streamed fit-extents path `v5Zoom = 1` so real text clears it.)
 * Realistic text counts (hundreds–low-thousands) make the always-lift
 * cost negligible.
 *
 * INSERT (`Block`) is NOT exempt (Codex PR #784 round-8 P2). It was, because a
 * dropped stub used to be unrecoverable; exempting it made THIS pass lift every
 * block reference in the drawing and recursively materialise each one's whole
 * `childRenderables` tree (1 000 000 children on a 20 000-INSERT synthetic).
 * A dropped Block stub is now recovered on demand: the spatial resolver falls
 * back to its metadata stub and `FrameLoops` Pass 2's lift publishes the
 * renderable + emits its direct meshes. Full mechanism in
 * `isAlwaysLiftedStubType` (lod/lodPolicy.ts).
 *
 * Every other stub keeps the EXACT prior behaviour: drop only `"skip"`
 * (sub-0.5 px), keep `"point"` / `"bbox-line"` / `"full"`; `droppedSkipCount`
 * counts only those non-exempt drops. See `isAlwaysLiftedStubType`.
 */
export function partitionStubsByLod(
  stubs: RenderableEntity[],
  fitZoom: number,
): { liftable: RenderableEntity[]; droppedSkipCount: number } {
  const liftable: RenderableEntity[] = [];
  let droppedSkipCount = 0;
  for (const stub of stubs) {
    // TEXT / MTEXT and DIMENSION / MULTILEADER (S9b Codex P2 — "Recreate
    // MLEADER decorators on LOD relift") are exempt from the sub-pixel drop —
    // see the JSDoc above and `isAlwaysLiftedStubType` (lod/lodPolicy.ts) for
    // the per-kind rationale. Block / INSERT is NOT (round-8 P2).
    if (isAlwaysLiftedStubType(stub.type)) {
      liftable.push(stub);
      continue;
    }
    if (lodBucketForEntity(stub, fitZoom) === "skip") {
      droppedSkipCount += 1;
      continue;
    }
    liftable.push(stub);
  }
  return { liftable, droppedSkipCount };
}

/**
 * Materialise every BatchedLayer bucket as a `LineSegments2` mesh and
 * register it as a single `BucketState` entry per bucket-id. Opt 8 —
 * one atomic `Map.set` per bucket instead of four parallel inserts.
 *
 * MC4 (model/paper separation) — each mesh is tagged with
 * `userData.spaceId` (BucketSpaceClass) so the layout render
 * orchestrator can pre-hide model-space geometry by the mesh's OWN
 * space, not by a layer-name heuristic. `spaceByBucketKeyId` is the
 * per-bucket classification computed by the caller from the
 * renderables' spaceIds; buckets absent from the map fall back to
 * `"model"` (the safe default for a model-only drawing — keeps the
 * pre-fix behaviour for any bucket the caller could not classify).
 */
export function buildBucketMeshes(args: {
  scene: THREE.Scene;
  gl: THREE.WebGLRenderer;
  size: { width: number; height: number };
  batched: BatchedLayer;
  buckets: Map<MeshKeyId, BucketState>;
  spaceByBucketKeyId?: ReadonlyMap<MeshKeyId, BucketSpaceClass>;
  /**
   * PR-LT3 — per-name LTYPE resolution map (`{ ...coreSeed, ...doc }`). The
   * per-bucket material resolves `bucket.key.linetype` → `DashConfig` against
   * it. Optional so existing callers / tests that don't supply it fall back to
   * the built-in standard registry (an empty map resolves every non-sentinel
   * name through `linetypeToDashConfig`).
   */
  linetypeResolution?: LinetypeResolutionMap;
  /**
   * FIX-1 (Codex P2) — per-layer LTYPE map (lower-cased layer name → linetype
   * NAME), built from `document.styleTables.layers[].linetype`. A bucket whose
   * linetype is the `ByLayer` sentinel resolves its dash pattern through THIS
   * map (the layer's assigned linetype). Optional so callers/tests that don't
   * supply it fall back to solid for `ByLayer` (the pre-FIX behaviour).
   */
  layerLinetypes?: LayerLinetypeMap;
  /**
   * PR-LT3 variant A (A2) — document linetype sysvars (LTSCALE / MSLTSCALE /
   * PSLTSCALE). Threaded into each bucket's material so the GLOBAL dash-scale
   * factor (selected by the bucket's space) rides the `dashScale` uniform.
   * Optional; absent → all-1 sysvars → `dashScale = 1` (pre-A2 behaviour).
   */
  linetypeSysvars?: LinetypeSysvars;
}): void {
  const {
    scene,
    gl,
    size,
    batched,
    buckets,
    spaceByBucketKeyId,
    linetypeResolution,
    layerLinetypes,
    linetypeSysvars,
  } = args;
  const EMPTY_LINETYPE_RESOLUTION: LinetypeResolutionMap = new Map();
  const resolution = linetypeResolution ?? EMPTY_LINETYPE_RESOLUTION;
  const EMPTY_LAYER_LINETYPES: LayerLinetypeMap = new Map();
  const layerLts = layerLinetypes ?? EMPTY_LAYER_LINETYPES;
  // Q18.B: detect WebGL1/WebGL2 once; pass to every per-bucket
  // selection state texture so the same compat mode is used across
  // the whole scene.
  const selectionCompat = detectSelectionStateCompat(gl);
  const pixelRatio = gl.getPixelRatio();
  for (const bucket of batched.getBuckets()) {
    buildSingleBucketMesh({
      scene,
      size,
      bucket,
      buckets,
      resolution,
      layerLinetypes: layerLts,
      selectionCompat,
      pixelRatio,
      spaceClass: spaceByBucketKeyId?.get(bucket.keyId) ?? "model",
      sysvars: linetypeSysvars,
    });
  }
}

/**
 * Build + register ONE BatchedLayer bucket as a `LineSegments2` mesh.
 *
 * # Why this is a named export (FIX-3 — Codex P2, relift new-bucket build)
 *
 * Now that `linetype` is part of the line `MeshKey`, the FrameLoops
 * viewport-cull relift path can encounter a NEW bucket key (same
 * layer/color, new linetype) for a newly-visible entity. The relift
 * reupload loop only updates buckets that ALREADY have a `BucketState`
 * and SKIPS unknown keys — so that new DASHED/CENTER/custom bucket
 * never got a mesh and the entity stayed INVISIBLE until the next full
 * bulk-load. The relift path now calls THIS helper to materialise the
 * new bucket on demand (per-newly-visible-entity, no full re-scan),
 * mirroring the bulk-load path exactly so linetype fidelity is
 * preserved.
 *
 * Returns the freshly registered `BucketState`, or `null` when the
 * bucket has no populated vertices (nothing to draw yet).
 */
export function buildSingleBucketMesh(args: {
  scene: THREE.Scene;
  size: { width: number; height: number };
  bucket: ReturnType<BatchedLayer["getBuckets"]>[number];
  buckets: Map<MeshKeyId, BucketState>;
  resolution: LinetypeResolutionMap;
  /**
   * FIX-1 — per-layer LTYPE map for `ByLayer` bucket resolution. Optional;
   * absent → `ByLayer` buckets render solid (the pre-FIX fallback).
   */
  layerLinetypes?: LayerLinetypeMap;
  selectionCompat: ReturnType<typeof detectSelectionStateCompat>;
  pixelRatio: number;
  spaceClass: BucketSpaceClass;
  /**
   * PR-LT3 variant A (A2) — FIX (Codex P2). Document linetype sysvars
   * (LTSCALE / MSLTSCALE / PSLTSCALE). The GLOBAL scale is folded PER-SEGMENT
   * into `instanceLtScale` by each segment's own space (`buildPerSegmentLtScales`),
   * NOT into a per-bucket `dashScale` uniform. Optional; absent → all-1 → global
   * scale 1 (per-segment value = `1 / CELTSCALE`, the pre-fold behaviour). The
   * FrameLoops relift path passes the per-pass sysvars captured at bulk-load so a
   * newly-materialised bucket matches the bulk-loaded buckets' scale.
   */
  sysvars?: LinetypeSysvars;
}): BucketState | null {
  const {
    scene,
    size,
    bucket,
    buckets,
    resolution,
    layerLinetypes,
    selectionCompat,
    pixelRatio,
    spaceClass,
    sysvars,
  } = args;
  // Codex P4 — `bucket.vertices` is now a Float32Array whose
  // capacity may exceed the populated range. Use `vertexLength` for
  // the actual count and subarray(0, length) to slice down to the
  // populated section before GPU upload — no double allocation.
  if (bucket.vertexLength === 0) return null;

  const positions = bucket.vertices.subarray(0, bucket.vertexLength);

  // Phase 2 Wave 1 (Task A) — recycle geometries via `meshPool` so
  // bulkLoad reruns reuse the same shell across passes. capacity is
  // measured in line-segment vertices: positions holds 6 floats per
  // segment (x,y,z × 2 endpoints).
  const geometry = acquireLineSegmentsGeometry(positions.length / 6);
  geometry.setPositions(positions);
  // Q18.B: the `instanceSlotIndex` attribute is what the state-aware shader
  // samples to look up the entity's state byte.
  //
  // fix/selection-highlight-not-applied — bound as a TRUE
  // `InstancedBufferAttribute` (one slot per SEGMENT), not the per-VERTEX
  // stream. `LineSegments2` draws one instance per segment off a fixed quad
  // prototype, so a non-instanced attribute is indexed by prototype vertex and
  // half of every quad sampled the wrong texel — a selected entity came out
  // partly repainted. See `buildPerSegmentSlotIndices`.
  const instanceAttr = new THREE.InstancedBufferAttribute(
    buildPerSegmentSlotIndices(bucket),
    1,
    false,
  );
  geometry.setAttribute("instanceSlotIndex", instanceAttr);
  // PR-LT3 variant A (A2) — FIX-2 (Codex P2). Per-SEGMENT CELTSCALE reciprocal
  // (group 48) bound as a TRUE INSTANCED attribute via `setLinetypeScales`
  // (divisor 1, aligned 1:1 with `instanceStart`/`instanceEnd`).
  //
  // The bucket's `instanceLtScales` is a PER-VERTEX stream (2 endpoints per
  // segment, both carrying the same per-entity reciprocal). `LineSegments2`
  // draws one INSTANCE per segment, so the instanced attribute needs exactly
  // ONE value per segment. Collapse the per-vertex pairs to per-segment by
  // sampling the START endpoint of each segment (every other vertex). This
  // matches `instanceStart.count` = segments = positions.length / 6.
  // PR-LT3 variant A (A2) — FIX (Codex P2). Fold the GLOBAL MS/PS LTSCALE into
  // the per-segment reciprocal BY EACH segment's own space (model uses
  // MSLTSCALE, paper uses the PSLTSCALE flag) and multiply in the per-entity
  // CELTSCALE reciprocal. A MIXED bucket renders each segment at its OWN space's
  // global scale → correct paper dash period even when PSLTSCALE differs. The
  // material `dashScale` uniform is a constant 1 (see lineMaterialDashSetup.ts).
  const perSegmentLtScales = buildPerSegmentLtScales(bucket, sysvars);
  geometry.setLinetypeScales(perSegmentLtScales);

  const colorHex = bucket.key.colorHex;

  // PR-LT3 — resolve the bucket's linetype NAME → material against the per-pass
  // resolution map (NON-STANDARD custom LTYPEs) + the standard mm registry.
  //
  // PR-LT3 variant A (A1) — the extracted `buildBucketLineMaterial` chooses the
  // SINGLE-PAIR fast path (continuous / faithful 2-element DASHED / HIDDEN) or
  // the FULL multi-element TEXTURE path (CENTER / DASHDOT / DOT / PHANTOM —
  // > 2 elements or a dot), encoding the pattern into a shared 1-D DataTexture
  // and flipping the material's USE_DASH_TEXTURE define. Material/dash setup was
  // hoisted out of this helper (file-size ceiling — see lineMaterialDashSetup.ts).
  //
  // FIX-1 — the bucket's LAYER + per-layer LTYPE map are threaded through so a
  // `ByLayer` bucket (the common case — CELTYPE default) resolves its pattern
  // through the layer's assigned linetype instead of falling to solid.
  //
  // The linewidth default (2 px) preserves the PR-F2 sub-pixel-coverage fix:
  // at linewidth=1 the thick-line half-quad is ~1 device pixel and can miss
  // every rasterizer sample (empty canvas); 2 px guarantees coverage.
  const { material } = buildBucketLineMaterial({
    linetypeName: bucket.key.linetype,
    layer: bucket.key.layer,
    resolution,
    layerLinetypes,
    colorHex,
    linewidth: 2,
    // Resolution must be set before first frame; useFrame keeps it in sync.
    resolutionPx: new THREE.Vector2(
      Math.max(size.width, 1) * pixelRatio,
      Math.max(size.height, 1) * pixelRatio,
    ),
  });

  // Q18.B: per-bucket selection state texture.
  //
  // Codex autocheck Finding 3 (P2) on PR #93 — pre-allocate
  // capacity with 2× headroom so per-frame viewport-cull re-lift
  // (which adds new bucket-local `instanceIndex` slots monotonically
  // via `BatchedLayer.addEntity`) doesn't overrun the texture mid-
  // frame. Without headroom, a fresh import that bulk-loads with
  // (say) 5 000 instances per bucket would crash at the first
  // 5 001st relift — `instanceIndex 5000` falls outside the
  // `texSize × texSize` slot grid and `setSelectedIds` writes
  // out-of-bounds into the underlying typed array (V8 silently
  // truncates / ignores the write, so the highlight visually
  // misses or paints the wrong entity).
  //
  // 2× covers the common relift load (zoom in → ~2× current visible
  // entities materialise) without doubling steady-state memory
  // beyond what an honest pre-allocation would have cost (the
  // texture is 1 byte / instance on WebGL2 ≈ ~1 KB per bucket for
  // a 1 000-instance bucket — negligible). Above the 2× threshold
  // FrameLoops' viewport-cull pass detects the overrun and either
  // calls `SelectionStateTexture.resize` (when implemented in a
  // follow-up) or no-ops gracefully.
  const selectionTexture = new SelectionStateTexture({
    capacity: Math.max(bucket.instanceCount * 2, 16),
    compat: selectionCompat,
  });
  patchMaterialWithSelectionState(material, {
    selectionStateTexture: selectionTexture,
  });

  const mesh = new LineSegments2(geometry, material);
  // Codex PR #785 round 12 P1 — the bucket's DRAWORDER stratum
  // (`drawOrderStrata.ts`). Stratum 0 evaluates to 0, the default every bucket
  // carried before strata existed, so a drawing that needs no cut is
  // byte-identical here; a later stratum lifts the whole bucket above the band
  // fills that were authored before it and below the ones authored after.
  mesh.renderOrder = lineStratumRenderOrder(bucket.key.drawOrderStratum ?? 0);
  // MC4 (model/paper separation) — carry the bucket's space class so
  // the layout orchestrator hides model-space buckets authoritatively
  // (by the mesh's own space), independent of the ambiguous
  // model-only-layer heuristic. Default "model" when unclassified.
  // Codex PR #784 round 3 P1 — the bucket's block-reference layer chain
  // (`MeshKey.parentLayers`) rides on BOTH the mesh userData (read by the
  // per-viewport mask, `applySceneVisibilityMask`) and the handle (read by
  // the global layer effect + the relift handle path) so a `(B, parents=[A])`
  // bucket hides when A is off / frozen on every path. Omitted for a plain
  // bucket so its userData / handle stay byte-identical to before.
  const parentLayers = bucket.key.parentLayers
    ? { parentLayers: bucket.key.parentLayers }
    : {};
  mesh.userData = {
    keyId: bucket.keyId,
    spaceId: spaceClass,
    ...parentLayers,
  };
  mesh.computeLineDistances();
  mesh.frustumCulled = false; // PR-C wires per-bucket bbox cull.
  scene.add(mesh);
  // Opt 8 — single atomic insert per bucket. PR-I — capture the
  // bucket's layer + base material flags so the visibility effect
  // can apply / unapply locked-layer fade without losing the bucket
  // builder's intent (e.g. hatch buckets that are already
  // transparent / depthWrite=false).
  const bucketState: BucketState = {
    mesh,
    material,
    selectionTexture,
    handle: {
      layer: bucket.key.layer,
      ...parentLayers,
      mesh: mesh as unknown as { visible: boolean },
      material: material as unknown as {
        opacity: number;
        transparent: boolean;
        depthWrite: boolean;
        needsUpdate?: boolean;
      },
      baseOpacity: material.opacity,
      baseTransparent: material.transparent,
      baseDepthWrite: material.depthWrite,
    },
  };
  buckets.set(bucket.keyId, bucketState);
  return bucketState;
}

/**
 * Build hatch / wide-polyline-band / wipeout / array-rect meshes for non-line
 * renderables and attach them to the scene.
 *
 * Opt 8 — writes into the consolidated `entityRenders` Map. Each entity gets
 * one `EntityRender.fillMesh` slot; an array-rect's InstancedMeshes are
 * collected in `EntityRender.arrayInstancedMeshes` on the ARRAY's own entry
 * (Codex PR #784 round-10 P2 — they used to live under per-mesh composite keys
 * `<entityId>::<uuid>`, which the LOD cull and layer effect cannot address).
 */
export function emitFillAndArrayMeshes(
  scene: THREE.Scene,
  renderables: RenderableEntity[],
  renderableById: Map<string, RenderableEntity>,
  entityRenders: Map<string, EntityRender>,
  v5Zoom: number,
  hiddenLayers?: ReadonlySet<string>,
  /**
   * The DRAWORDER strata the owning `BatchedLayer.bulkLoad` planned
   * (`BatchedLayerApi.getDrawOrderStrata`) — the band fill is ordered against
   * the line buckets through it. Omitted → one stratum, i.e. the single-band
   * stack the constant `-0.5` used to encode.
   */
  drawOrderStrata: DrawOrderStrata = SINGLE_DRAW_ORDER_STRATUM,
): void {
  for (const renderable of renderables) {
    if (renderable.geometry.kind === "hatch") {
      const fillMesh = buildHatchFillMesh(renderable, v5Zoom);
      if (fillMesh) {
        attachFillMesh(scene, renderable, fillMesh, -2, entityRenders, hiddenLayers);
      }
    } else if (renderable.geometry.kind === "polyline") {
      // @codex PR #785 round 11 F2 — a polyline carrying DXF WIDTH is drawn as
      // a SOLID band when the drawing's `$FILLMODE` is on (the Autodesk
      // documented initial value 1, which is also what an absent var means).
      // This mesh is the band's INTERIOR only: `batchedLayer.ts` strokes the
      // silhouette into a line bucket in BOTH fill states (that outline
      // carries the selection byte and the minimum on-screen width — see its
      // own doc), and the mesh renders UNDER it. At `$FILLMODE` 0 the builder
      // returns `null` and the outline is the whole entity. A width-LESS
      // polyline also returns `null` and is unchanged — a centreline in the
      // line buckets exactly as before.
      const fillMesh = buildWidePolylineFillMesh(renderable);
      if (fillMesh) {
        attachFillMesh(
          scene,
          renderable,
          fillMesh,
          widePolylineFillRenderOrder(drawOrderStrata, renderable),
          entityRenders,
          hiddenLayers,
        );
      }
    } else if (renderable.geometry.kind === "wipeout") {
      const fillMesh = buildWipeoutFillMesh(renderable);
      if (fillMesh) {
        attachFillMesh(scene, renderable, fillMesh, -1, entityRenders, hiddenLayers);
      }
    } else if (renderable.geometry.kind === "dimension") {
      // PR-F P1 — emit arrow / tick InstancedMeshes per
      // `(variant, colorHex)` group. Text primitives are synthesised
      // into RenderableEntity records in `emitTextMeshes` so they
      // flow through the same SDF / SHX text pipeline.
      attachArrowDecoratorMeshes(
        scene,
        renderable,
        buildDimensionArrowMeshes(renderable),
        entityRenders,
        hiddenLayers,
      );
    } else if (renderable.geometry.kind === "multileader") {
      // S9b — emit the leader arrow-head decorator meshes. Leader lines /
      // dogleg / landing flow through the BatchedLayer line pipeline
      // (`geometryToLineVertices`), `mtext-content` is synthesised in
      // `emitTextMeshes`, and `block-content` is expanded into first-class
      // child renderables at bulk-load — so only the arrow heads need a
      // direct-attach mesh here.
      attachArrowDecoratorMeshes(
        scene,
        renderable,
        buildMultileaderArrowMeshes(renderable),
        entityRenders,
        hiddenLayers,
      );
    } else if (renderable.geometry.kind === "array-rect") {
      // PR-D + Q12.B: emit one InstancedMesh per resolved base
      // entity. The base entity's renderable must already be in the
      // scene (it's an ordinary line / circle / polyline with its
      // own line-bucket entry). We only attach a translation matrix
      // per cell — the base mesh's geometry/material are reused via
      // an InstancedMesh wrapper. If a base entity id cannot be
      // resolved (e.g. the source block is detached) we skip.
      const arrayMeshes = buildArrayInstancedMeshes(renderable, renderableById);
      if (arrayMeshes.length > 0) {
        // FIX (Codex PR #784 round-10 P2 — "Cull relifted array meshes through
        // their parent entry"): register every emitted InstancedMesh on the
        // ARRAY ENTITY's OWN render entry. They used to go under one composite
        // key per mesh (`<entityId>::<uuid>`) so dispose could free each
        // independently — but NOTHING else can address a composite key: both
        // the per-frame LOD cull / re-show (`FrameLoops`) and the layer effect
        // look an entity up by its own id and re-apply
        // `applyEntityRenderVisibility`, so a culled array kept painting after
        // its spatial entity had been removed from the buckets. This is the ONE
        // array emit site, so the hole existed on the bulk-load path as much as
        // on the relift path; both are closed here. `sceneTeardown` still frees
        // each mesh individually, walking the list.
        const render = upsertEntityRender(entityRenders, renderable.id);
        const meshes = (render.arrayInstancedMeshes ??= []);
        for (const m of arrayMeshes) {
          m.frustumCulled = false;
          scene.add(m);
          meshes.push(m);
        }
        // Same emit-time bookkeeping every other direct mesh gets: the layer
        // chain the composite visibility reads, the SYNC-channel idempotence
        // marker, and the current hidden-layer state applied immediately.
        recordEntityRenderLayers(render, renderable);
        render.directMeshesEmitted = true;
        if (hiddenLayers) applyEntityRenderVisibility(render, hiddenLayers);
      }
    }
  }
}

/**
 * PR-E2/E5 — build text meshes (SDF instanced or SHX strokes), with
 * placeholder rectangles when the font is unavailable. Async path:
 * each font group resolves on its own promise; the build token guards
 * stale builds when the entity feed re-bulkLoads while a fetch is
 * mid-flight.
 *
 * @codex #334 (P2-B) — `styleFontByName` maps a lowercased STYLE name →
 * the STYLE's actual `fontName` (DXF group 3, e.g. `gost.shx`). A
 * generically-named STYLE (`name=Standard`, `fontName=gost.shx`) only
 * forwards its NAME ("Standard") on `r.geometry.styleName`, so without
 * this lookup the GOST/ISOCPEUR font file is invisible to dispatch.
 * Resolve `styleName` → the STYLE's `fontName` (falling back to the
 * styleName itself when the STYLE / its fontName is absent — the
 * pre-fix behaviour) BEFORE `resolveDispatchFontName`.
 */
export function buildStyleFontMap(
  textStyles: readonly TextStyle[] | undefined,
): Map<string, string> {
  // Dispatch on the STYLE's `fontName` — in this model that IS the DXF group-3
  // font file (the importer writes group 3 → `fontName`,
  // `dxfBridge.drawingToDxf`), AND it is the field the Text Style editor reads
  // and writes (`TextStyleEditorDialog` → `updateTextStyle({ fontName })`). The
  // sibling schema field `fontFile` is vestigial — its only writer sets it to
  // "" — so preferring it would (a) be a no-op today and (b) let a future stale
  // `fontFile` override an editor `fontName` edit (@codex #334). Entries with no
  // `fontName` are skipped (dispatch then falls back to the styleName).
  const map = new Map<string, string>();
  for (const style of textStyles ?? []) {
    const fontRef = style.fontName?.trim();
    if (fontRef) map.set(style.name.toLowerCase(), fontRef);
  }
  return map;
}

export function emitTextMeshes(
  scene: THREE.Scene,
  renderables: RenderableEntity[],
  entityRenders: Map<string, EntityRender>,
  textBuildTokenRef: { current: number },
  v5Zoom: number,
  invalidate: () => void,
  styleFontByName?: ReadonlyMap<string, string>,
  // FIX (Codex S9b P2 — "Recompute hidden layers for async text builds"): a REF,
  // not a snapshot Set. Text builds ASYNC (font resolution), and
  // `finishSceneBulkLoad` runs the end-of-pass `applyAndMirrorLayerVisibility`
  // (which REPLACES `hiddenLayersRef.current`) — plus any interleaved layer
  // toggle — BEFORE this promise resolves. Reading `.current` at RESOLUTION time
  // (not schedule time) means a layer hidden while the font was in flight is
  // honoured, so the label is not created visible on an already-hidden layer.
  hiddenLayersRef?: { current: ReadonlySet<string> },
): void {
  // PR-F P1 — pre-pass: walk dimension renderables, synthesise one
  // text RenderableEntity per `text` primitive and append into the
  // text-renderables list. This routes dimension measurement text
  // through the same SDF / SHX pipeline used for real TEXT/MTEXT.
  // Synthesised ids are namespaced (`${parentId}::text::${i}`) so the
  // bulk-load text registry never collides with a real entity id.
  // S9b — MLEADER `mtext-content` is synthesised the same way (into an
  // `mtext` RenderableEntity) so the leader label flows through the same
  // MTEXT text pipeline. (Block-content text children are already
  // first-class renderables in the feed — see `finishSceneBulkLoad` — so
  // they are picked up by the `text` / `mtext` filter below directly.)
  const synthesisedDecoratorTexts: RenderableEntity[] = [];
  // Codex PR #784 round-11 P1 — every text RUN's SOURCE renderable: the feed
  // entity a retry must re-submit to rebuild that run. A synthesised label maps
  // to its PARENT (which synthesises it and owns its mesh); a real TEXT / MTEXT
  // maps to itself. See `pendingTextRetry.ts`.
  const sourceByRunId = new Map<string, RenderableEntity>();
  for (const r of renderables) {
    if (r.geometry.kind === "dimension") {
      const t = synthesizeDimensionTextRenderables(r);
      for (const s of t) {
        synthesisedDecoratorTexts.push(s);
        sourceByRunId.set(s.id, r);
      }
    } else if (r.geometry.kind === "multileader") {
      const t = synthesizeMultileaderTextRenderables(r);
      for (const s of t) {
        synthesisedDecoratorTexts.push(s);
        sourceByRunId.set(s.id, r);
      }
    }
  }
  const textRenderables = renderables.filter(
    (r) => r.geometry.kind === "text" || r.geometry.kind === "mtext",
  );
  for (const s of synthesisedDecoratorTexts) textRenderables.push(s);
  if (textRenderables.length === 0) return;

  // S9b (Codex P2 — "Hide synthetic labels when culling parents"): the SYNTHETIC
  // decorator labels (dimension `::text::`, MLEADER `::mtext::`) must ride on
  // the PARENT `EntityRender` so the per-frame LOD cull hides them with the
  // leader. A block-content TEXT child ALSO carries `renderParentId` (for the VP
  // mask) but is a first-class feed renderable tracked via its OWN render (the
  // cull's block-children path), so it must KEEP its own render — hence key the
  // routing on "was this SYNTHESISED here", not on `renderParentId` presence.
  const syntheticDecoratorIds = new Set(
    synthesisedDecoratorTexts.map((s) => s.id),
  );

  // FIX (Codex PR #784 round-10 P1 — "Retry text meshes skipped during an
  // earlier relift"): per-text-run emission state, keyed by the TEXT
  // renderable's OWN id (a real TEXT / MTEXT entity, or a synthesised
  // `${parent}::text::i` decorator label whose MESH rides on the parent).
  //
  // `claimTextEmit` is the sole gate on scheduling a build. It is SYNCHRONOUS,
  // so it closes the async double-emit window (a cull + relift while a font
  // fetch is in flight would otherwise queue a second mesh for the same run and
  // leak the first into the scene) — the window the relift emitter previously
  // closed by pre-writing `EntityRender.entityLayer`.
  //
  // The difference is what happens when the build produces NOTHING:
  // `buildTextEntityMesh` / `buildShxTextEntityMesh` return `null` for the PR-V5
  // sub-pixel gate (`skip` / `dot`), which is a function of the CURRENT camera /
  // viewport scale. `settleTextEmit(r, false)` therefore RELEASES the claim, so
  // the run is emitted by a later pass at a larger scale (a satellite that was
  // still sub-pixel when its parent first crossed the LOD threshold used to be
  // marked "already emitted" and stayed absent until the next full bulk load).
  //
  // Codex PR #784 round-11 P1 — releasing the claim is only HALF of a retry:
  // nothing re-enters this function for a run whose parent stays lifted
  // (`FrameLoops` Pass 2 skips the resolver for every already-lifted id), so a
  // released run is also REGISTERED as pending, with the scale it failed at and
  // the id whose lift owns it. `scene/FrameLoops.tsx` drains that registry
  // (`retryPendingTextRuns`) on the frames where the governing scale actually
  // grew — O(skipped runs), with no walk over the lifted set. The retry
  // re-enters HERE with the run's SOURCE renderable, which is why this pass's
  // emit closure is registered alongside it.
  let retryEmit: ((sources: RenderableEntity[], zoom: number) => void) | null =
    null;
  const claimTextEmit = (r: RenderableEntity): boolean => {
    if (entityRenders.get(r.id)?.textEmit !== undefined) return false;
    upsertEntityRender(entityRenders, r.id).textEmit = "pending";
    // Being attempted right now: a skip re-registers it below with the scale
    // just tried, and a success leaves it gone for good.
    releasePendingTextRun(entityRenders, r.id);
    return true;
  };
  const settleTextEmit = (r: RenderableEntity, emitted: boolean): void => {
    const render = entityRenders.get(r.id);
    if (!render) return;
    if (emitted) {
      render.textEmit = "emitted";
      return;
    }
    delete render.textEmit;
    const source = sourceByRunId.get(r.id) ?? r;
    retryEmit ??= (sources, zoom): void => {
      emitTextMeshes(
        scene,
        sources,
        entityRenders,
        textBuildTokenRef,
        zoom,
        invalidate,
        styleFontByName,
        hiddenLayersRef,
      );
    };
    registerPendingTextRun(
      entityRenders,
      r.id,
      {
        source,
        // The spatial-index id a viewport names in `ViewportLiftRequests.lift`:
        // a satellite's own id is never in that map, its INSERT's is.
        ownerId: source.renderParentId ?? source.id,
        scale: v5Zoom,
      },
      retryEmit,
    );
    // A gated-out run leaves NO residue: a claim-only entry (the common case
    // for a synthesised label, and for any real text whose own mesh never
    // materialised) is dropped rather than retained for every sub-pixel label
    // in a large drawing.
    if (Object.keys(render).length === 0) entityRenders.delete(r.id);
  };
  const pendingTextRenderables = textRenderables.filter(claimTextEmit);
  if (pendingTextRenderables.length === 0) return;

  // Register a freshly-built text mesh: a synthetic decorator label rides on its
  // PARENT render's `satelliteTextObjects` (so the cull + layer effect toggle it
  // via the parent, and teardown disposes it there); every other text mesh (real
  // TEXT/MTEXT, block-content text child) owns its `EntityRender.textObject`. In
  // BOTH cases the CURRENT hidden-layer set (read live off the ref — see the
  // `hiddenLayersRef` doc) drives the initial `.visible`.
  const registerTextMesh = (
    mesh: THREE.Object3D,
    renderable: RenderableEntity,
  ): void => {
    const hidden = hiddenLayersRef?.current;
    if (
      syntheticDecoratorIds.has(renderable.id) &&
      renderable.renderParentId !== undefined
    ) {
      // Attach to the parent render — inherit its composite (layer AND lod)
      // visibility so a parent culled DURING the async font load hides the label
      // too. `renderable.layer` / `renderParentLayers` are the parent's (the
      // synthesiser copies them), so set the parent's layer fields if the
      // parent had no direct mesh yet (arrow-less leader) to keep layer hiding
      // working.
      const parentRender = upsertEntityRender(
        entityRenders,
        renderable.renderParentId,
      );
      if (parentRender.entityLayer === undefined) {
        recordEntityRenderLayers(parentRender, renderable);
      }
      (parentRender.satelliteTextObjects ??= []).push(mesh);
      scene.add(mesh);
      applyEntityRenderVisibility(parentRender, hidden ?? EMPTY_HIDDEN_LAYERS);
      return;
    }
    scene.add(mesh);
    // Opt 8 — store under the renderable id; preserves other fields (e.g.
    // fillMesh / textureSlot / lodState) already written for this entity.
    const render = upsertEntityRender(entityRenders, renderable.id);
    render.textObject = mesh;
    recordEntityRenderLayers(render, renderable);
    // FIX (Codex S9b round-5 P2 — "Apply LOD visibility to late text meshes"):
    // a real TEXT/MTEXT (or block-content text child) builds its mesh ASYNC. If
    // the per-frame viewport-cull marked this entity's render `lodHidden` while
    // the font promise was in flight — OR the block-child cull pre-seeded a
    // hidden render before any mesh existed (`FrameLoops`) — a plain
    // hidden-LAYER check would insert the mesh VISIBLE on a culled entity,
    // orphaning it until the next camera / layer update. Apply the COMPOSITE
    // (lod AND layer) visibility so the existing `lodHidden` flag is honoured at
    // resolution time (subsumes the old hidden-layer-only `.visible` write).
    applyEntityRenderVisibility(render, hidden ?? EMPTY_HIDDEN_LAYERS);
  };

  const buildToken = textBuildTokenRef.current;
  // PR-E5 — group by dispatch font name and route via `resolveFont`.
  const byFont = new Map<string, RenderableEntity[]>();
  for (const r of pendingTextRenderables) {
    const styleName =
      r.geometry.kind === "text" || r.geometry.kind === "mtext"
        ? r.geometry.styleName
        : undefined;
    // @codex #334 (P2-B) — resolve the STYLE name to its actual fontName
    // (DXF group 3) when the STYLE table carries one; fall back to the
    // styleName itself (the pre-fix dispatch input) when absent. This is
    // what lets a `name=Standard, fontName=gost.shx` STYLE map to GOST.
    const requested =
      styleName !== undefined
        ? styleFontByName?.get(styleName.toLowerCase()) ?? styleName
        : undefined;
    const fontName = resolveDispatchFontName(requested);
    const arr = byFont.get(fontName);
    if (arr) arr.push(r);
    else byFont.set(fontName, [r]);
  }
  for (const [fontName, group] of byFont) {
    resolveFont(fontName)
      .then((resource) => {
        // Stale build guard — entity feed changed since this
        // promise was scheduled. No claim release here on purpose: the token is
        // bumped only by a bulk-load / unmount pass that ALSO clears
        // `entityRenders` (`sceneTeardown`), so no claim can survive it — while
        // releasing would clobber a fresh claim the new pass has already taken
        // for the same id.
        if (textBuildTokenRef.current !== buildToken) return;
        for (const renderable of group) {
          const mesh =
            resource.kind === "sdf"
              ? buildTextEntityMesh(renderable, resource.atlas, v5Zoom)
              : buildShxTextEntityMesh(renderable, resource.strokes, v5Zoom);
          if (mesh) {
            mesh.renderOrder = 2;
            mesh.frustumCulled = false;
            // FIX (Codex S9b P2 — "Hide synthesized MLEADER text with its
            // layer" + "Hide synthetic labels when culling parents"): route the
            // mesh to its own render OR the parent's satellite list, applying the
            // CURRENT hidden-layer set (+ the parent's lod state, for a
            // synthetic label) — see `registerTextMesh`.
            registerTextMesh(mesh, renderable);
          }
          // `null` here is the PR-V5 scale gate (or a degenerate run): release
          // the claim so a later, larger-scale emit rebuilds this run.
          settleTextEmit(renderable, mesh !== null);
        }
        invalidate();
      })
      .catch((err) => {
        if (textBuildTokenRef.current !== buildToken) return;
        if (err instanceof MissingFontError) {
          // Q15.C — neither an SDF atlas nor an SHX font is
          // available. Emit placeholder rectangles per entity so
          // the user knows text is present at this location.
          console.warn(
            `[scene3d.text] missing font '${fontName}': ${err.message}`,
          );
          for (const renderable of group) {
            const placeholder = buildTextPlaceholderMesh(renderable);
            if (placeholder) {
              placeholder.renderOrder = 2;
              placeholder.frustumCulled = false;
              // Same routing as the success path (own render vs parent
              // satellite) + current-hidden-layer application.
              registerTextMesh(placeholder, renderable);
            }
            // A placeholder IS an emitted mesh; its absence releases the claim
            // exactly as a gated-out glyph build does.
            settleTextEmit(renderable, placeholder !== null);
          }
          invalidate();
        } else {
          console.warn("[scene3d.text] unexpected loader error", err);
          // Nothing was built and nothing will be: release every claim in the
          // group so a later emit (relift / next bulk load) can retry rather
          // than seeing a permanently "pending" run.
          for (const renderable of group) settleTextEmit(renderable, false);
        }
      });
  }
}

/**
 * Geometry kinds `emitFillAndArrayMeshes` branches on — the SYNCHRONOUS direct
 * mesh channel (hatch / wide-polyline band / wipeout fill, dimension /
 * multileader arrow heads, array-rect instances). A renderable of any other
 * kind contributes nothing here; its line slot, which
 * `BatchedLayer.addEntity` rebuilds on its own, fully describes it.
 */
function carriesDirectFillMeshes(r: RenderableEntity): boolean {
  // A POLYLINE reaches this channel ONLY when it is actually drawn as a FILLED
  // band. Its fill mesh is a direct scene object this channel owns, so a relift
  // that skipped it would bring the entity back as a HOLLOW ribbon — its
  // silhouette restored by `BatchedLayer.addEntity`, its interior gone — until
  // the next full bulk load. The test is deliberately the fill builder's own
  // gate minus the triangulation, two field reads: answering `true` for EVERY
  // polyline would make `emitReliftDirectMeshes` allocate an `EntityRender` and
  // record a layer chain for the single most common entity kind in the drawing
  // on every LOD relift, to emit nothing.
  if (r.geometry.kind === "polyline") {
    return (
      polylineDeclaresBandWidth(r.geometry) && fillModePaintsSolid(r.fillmode)
    );
  }
  switch (r.geometry.kind) {
    case "hatch":
    case "wipeout":
    case "dimension":
    case "multileader":
    case "array-rect":
      return true;
    default:
      return false;
  }
}

/**
 * Geometry kinds `emitTextMeshes` branches on — the ASYNCHRONOUS glyph channel:
 * a real TEXT / MTEXT entity, plus DIMENSION / MULTILEADER, whose measurement /
 * `mtext-content` labels that emitter SYNTHESISES into text renderables of
 * their own.
 */
function carriesTextMeshes(r: RenderableEntity): boolean {
  switch (r.geometry.kind) {
    case "text":
    case "mtext":
    case "dimension":
    case "multileader":
      return true;
    default:
      return false;
  }
}

/**
 * Codex PR #784 round-8 P2 (`lod/lodPolicy.ts:217`) — PASS-2 RELIFT EMISSION.
 *
 * `FrameLoops` Pass 2 lifts a newly-visible entity and hands it to
 * `BatchedLayer.addEntity`, which feeds LINE vertices only. Every other render
 * an entity owns — a HATCH / WIPEOUT fill, a DIMENSION / MULTILEADER arrow
 * head, a TEXT / MTEXT glyph mesh, an array-rect InstancedMesh — is a DIRECT
 * scene object built exclusively by `emitFillAndArrayMeshes` / `emitTextMeshes`
 * at bulk load. For an entity that was ALREADY bulk-lifted and merely culled
 * that is fine (the mesh survives; the cull only hid it). For an entity lifted
 * for the FIRST time by Pass 2 — which is every stub the LOD pre-classify
 * dropped as sub-pixel, including a sub-pixel INSERT's flattened children —
 * the mesh does not exist yet, so without this the reference would come back
 * from zoom as bare lines: no fills, no labels, no arrow heads.
 *
 * This emits exactly the missing meshes, through the SAME two emitters the
 * bulk-load path uses (one truth — no parallel mesh builder), for the parent
 * AND its render-only satellites (the caller passes the expanded feed).
 *
 * IDEMPOTENCE — two channels, two markers (Codex PR #784 round-10 P1). The
 * pre-fix code used ONE: `EntityRender.entityLayer`, pre-written here before
 * either emitter ran. That conflated "emitted" with "attempted", and the text
 * builders legitimately emit NOTHING for a run the PR-V5 scale gate puts in the
 * `skip` / `dot` bucket — so a satellite label that was still sub-pixel when its
 * parent first crossed the LOD threshold (a big line lifts the INSERT before a
 * small label is legible, or a viewport magnifies the label while the main
 * camera stays zoomed out) was marked emitted and never retried.
 *
 *   - SYNC channel (fill / arrows / array instances) —
 *     `EntityRender.directMeshesEmitted`, set by every attach site AND here.
 *     Those builders are scale-INDEPENDENT (the PR-V5 hatch gate downgrades a
 *     pattern to a flat solid rather than emitting nothing; an array with no
 *     resolvable base still emits its placeholder), so "attempted" and
 *     "emitted" coincide and one boolean is exactly right.
 *   - ASYNC text channel — `emitTextMeshes` owns per-text-run
 *     `EntityRender.textEmit` state (`"pending"` while a font promise is in
 *     flight — which is what closes the double-emit window — `"emitted"` once a
 *     mesh exists, and RELEASED when a build produced nothing). So this helper
 *     passes every text-carrying renderable through on EVERY relift and lets
 *     that state decide: an already-emitted run is skipped, a gated-out one is
 *     re-evaluated at the current `v5Zoom`. A gated-out run is ALSO registered
 *     in the pending-retry index (`pendingTextRetry.ts`, round-11 P1), because
 *     a relift is not guaranteed to happen again: once the parent is lifted,
 *     `FrameLoops` Pass 2 stops calling the resolver for it, so the frame loop
 *     drains that index directly when the governing scale grows.
 *
 * The layer chain is still recorded up front (same value the attach sites
 * record) so a pending mesh honours layer visibility from the moment it is
 * queued.
 *
 * `v5Zoom` is the LIVE camera zoom (PR-V5 emit-time text-height / hatch-pattern
 * gate), read by the caller at relift time — not the zoom the bulk load ran at.
 */
export function emitReliftDirectMeshes(args: {
  scene: THREE.Scene;
  /** Parent + its render-only satellites (`expandRenderSatellites`). */
  feed: ReadonlyArray<RenderableEntity>;
  /** This pass's id → renderable map (array-rect resolves its base through it). */
  renderableById: Map<string, RenderableEntity>;
  entityRenders: Map<string, EntityRender>;
  textBuildTokenRef: { current: number };
  v5Zoom: number;
  invalidate: () => void;
  styleFontByName: ReadonlyMap<string, string>;
  hiddenLayersRef: { current: ReadonlySet<string> };
  /**
   * The live `BatchedLayer`'s DRAWORDER strata plan. A relift resolves its
   * entity's stratum through the SAME plan the bulk load cut — the resolver is
   * a binary search over the plan's boundaries, so an entity the plan never
   * saw still lands where its `(zOrder, id)` says it belongs.
   */
  drawOrderStrata?: DrawOrderStrata;
}): void {
  const fillPending: RenderableEntity[] = [];
  const textPending: RenderableEntity[] = [];
  for (const r of args.feed) {
    const emitsFill = carriesDirectFillMeshes(r);
    const emitsText = carriesTextMeshes(r);
    if (!emitsFill && !emitsText) continue;
    const render = upsertEntityRender(args.entityRenders, r.id);
    recordEntityRenderLayers(render, r);
    if (emitsFill && render.directMeshesEmitted !== true) {
      // Claimed synchronously; `emitFillAndArrayMeshes` cannot re-enter this
      // helper, and every one of its attach sites sets the same marker.
      render.directMeshesEmitted = true;
      fillPending.push(r);
    }
    if (emitsText) textPending.push(r);
  }
  if (fillPending.length > 0) {
    emitFillAndArrayMeshes(
      args.scene,
      fillPending,
      args.renderableById,
      args.entityRenders,
      args.v5Zoom,
      args.hiddenLayersRef.current,
      args.drawOrderStrata ?? SINGLE_DRAW_ORDER_STRATUM,
    );
  }
  if (textPending.length > 0) {
    emitTextMeshes(
      args.scene,
      textPending,
      args.entityRenders,
      args.textBuildTokenRef,
      args.v5Zoom,
      args.invalidate,
      args.styleFontByName,
      args.hiddenLayersRef,
    );
  }
}

/**
 * Fit the orthographic camera frustum to the union AABB of the freshly
 * built renderables. PR-B placeholder; replaced by the SceneCamera
 * reducer in PR-B+.
 */
export function fitOrthographicCameraToRenderables(
  camera: THREE.Camera,
  renderables: RenderableEntity[],
  width: number,
  height: number,
): void {
  if (renderables.length === 0) return;
  if (!(camera as THREE.OrthographicCamera).isOrthographicCamera) return;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const r of renderables) {
    if (r.bounds.minX < minX) minX = r.bounds.minX;
    if (r.bounds.minY < minY) minY = r.bounds.minY;
    if (r.bounds.maxX > maxX) maxX = r.bounds.maxX;
    if (r.bounds.maxY > maxY) maxY = r.bounds.maxY;
  }
  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY)
  )
    return;
  const w = Math.max(maxX - minX, 1);
  const h = Math.max(maxY - minY, 1);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const aspect = Math.max(width, 1) / Math.max(height, 1);
  // Fit so the wider axis dominates; 1.05× margin keeps strokes off
  // the edge.
  const halfW = Math.max(w * 0.55, h * 0.55 * aspect);
  const halfH = halfW / aspect;
  const ortho = camera as THREE.OrthographicCamera;
  ortho.left = -halfW;
  ortho.right = halfW;
  ortho.top = halfH;
  ortho.bottom = -halfH;
  ortho.position.set(cx, cy, 100);
  ortho.zoom = 1;
  ortho.updateProjectionMatrix();
  ortho.lookAt(cx, cy, 0);
}

/**
 * Apply a parsed DXF VPORT *Active saved view to the orthographic
 * camera. AutoCAD's saved view defines the visible CONTENT box (group
 * code 40 = view height, 41 = aspect = width/height) — that rectangle
 * must be fully VISIBLE inside the frustum (no clipping at the rect
 * edges). The frustum aspect itself must match the viewport's pixel
 * aspect, otherwise circles render as ovals. We pick the smaller
 * frustum that contains the saved-view box AND has viewport aspect —
 * the same "max" pattern that `fitOrthographicCameraToRenderables`
 * uses above. No 1.05× margin like zoom-extents adds; the saved-view
 * box still defines the lower bound of the visible extent.
 *
 * DCS-vs-WCS contract: by the time `savedView` reaches this helper,
 * the bridge importer (`extractSavedViewFromRawTableGroups` in
 * `dxfBridge.import.ts`) has already filtered out non-plan-view DXFs
 * (anything where group codes 16/26/36 ≠ (0,0,1) within 1e-6). So
 * `savedView.center` is guaranteed to be WCS — DCS and WCS coincide
 * for plan view. Non-plan files fall through to zoom-extents instead.
 *
 * Returns `true` when the camera was mutated, `false` when the saved
 * view was rejected (non-ortho camera, non-plan viewDirection, or
 * non-finite / non-positive values). Callers must branch on this so
 * a rejected savedView does NOT leave the camera at its default seed
 * `(0, 0, 100) zoom=1` — they should fall back to zoom-extents.
 */
export function applySavedViewToOrthographicCamera(
  camera: THREE.Camera,
  savedView: SavedView,
  width: number,
  height: number,
  /**
   * Scene3D subtracts a per-import origin from every entity WCS coord
   * for Float32 precision. Camera must live in the same offset space
   * or the savedView center lands millions of units from the rendered
   * geometry. Caller passes `getCoordinateOrigin()`; helper falls back
   * to (0,0) when null (synthetic / test cases).
   */
  originOffset?: { x: number; y: number } | null,
): boolean {
  if (!(camera as THREE.OrthographicCamera).isOrthographicCamera) return false;
  // Defense-in-depth: importer already drops savedView for non-plan
  // views, but if a caller bypasses the bridge guard we still refuse
  // to apply DCS coordinates as WCS. AutoCAD VPORT group codes 12/22
  // are DCS; they coincide with WCS only when viewDirection is
  // PARALLEL to +Z — any positive Z magnitude with negligible XY
  // components qualifies (DXF emitters write (0,0,1) or (0,0,N)).
  const vd = savedView.viewDirection;
  if (vd !== undefined) {
    const VIEW_DIR_EPS = 1e-6;
    const planar =
      vd.z > 0 &&
      Math.abs(vd.x) <= VIEW_DIR_EPS * vd.z &&
      Math.abs(vd.y) <= VIEW_DIR_EPS * vd.z;
    if (!planar) return false;
  }
  const ox = originOffset?.x ?? 0;
  const oy = originOffset?.y ?? 0;
  const cx = savedView.center.x - ox;
  const cy = savedView.center.y - oy;
  const boxH = savedView.height;
  const boxW = savedView.height * savedView.aspectRatio;
  if (
    !Number.isFinite(cx) ||
    !Number.isFinite(cy) ||
    !Number.isFinite(boxH) ||
    !Number.isFinite(boxW) ||
    boxH <= 0 ||
    boxW <= 0
  )
    return false;
  const viewportAspect = Math.max(width, 1) / Math.max(height, 1);
  // Pick the smaller frustum that contains the saved-view box AND has
  // viewportAspect. Either width-bound (boxW dominates) or height-bound
  // (boxH * viewportAspect dominates), whichever is larger — same
  // "max" pattern as fitOrthographicCameraToRenderables.
  const frustumW = Math.max(boxW, boxH * viewportAspect);
  const frustumH = frustumW / viewportAspect;
  const halfW = frustumW / 2;
  const halfH = frustumH / 2;
  const ortho = camera as THREE.OrthographicCamera;
  ortho.left = -halfW;
  ortho.right = halfW;
  ortho.top = halfH;
  ortho.bottom = -halfH;
  ortho.position.set(cx, cy, 100);
  ortho.zoom = 1;
  ortho.updateProjectionMatrix();
  ortho.lookAt(cx, cy, 0);
  return true;
}

/**
 * Per-document camera auto-fit claim state (issue #225; reshaped by the
 * #235 follow-up @codex P2 pair — see `shouldAutoFitCamera`).
 *
 * `runSceneBulkLoad` claims this on EVERY bulk-load pass (after the fit
 * gate has read the PRE-claim value) via `claimAutoFitDocument`:
 *
 *   - `documentId` — the `geometryDocument.id` the most recent pass
 *     rendered. Claiming on every pass (not only on fit) is the #235
 *     reset-then-draw fix: a freshly reset/new EMPTY drawing records the
 *     constant seed id, so the user's subsequent same-id draws compare
 *     equal and never trip a fit — AutoCAD never auto-zooms while you
 *     draw into a new drawing.
 *   - `contentSeen` — whether ANY pass since `documentId` was claimed
 *     produced renderables. This is the #235 follow-up fix for the
 *     streamed-OPEN P2: a streaming import commits the new
 *     `geometryDocument` during the sync phase and rebuilds
 *     `entityMetadataIndex` afterwards, so the scene effect runs once
 *     with the new id but ZERO renderables. That pre-pass claims the id
 *     with `contentSeen: false`; when the metadata-index bump re-runs
 *     the effect and the cache-direct lift materialises the content,
 *     the gate sees `useCacheDirect && !contentSeen` and still fires the
 *     document's one fit. Without the flag the pre-pass consumed the
 *     fit and the streamed file stayed off-screen.
 *
 * Replaces the pre-fix `cameraEverInitializedRef` (lifetime one-shot
 * latch) + `cameraFitDocumentIdRef` (bare id) pair: the lifetime latch's
 * `!everInitialized` OR-branch in the gate was the second #235 follow-up
 * P2 (it forced a fit on the 2nd entity drawn into a start-from-empty
 * document despite the id matching), and once removed from the gate the
 * latch had no readers left. A single composite ref also makes the
 * id + contentSeen claim atomic — the two fields can never desync.
 */
export interface CameraFitDocState {
  /** `geometryDocument.id` claimed by the most recent bulk-load pass. */
  documentId: string;
  /**
   * `true` once any pass for this claim produced `renderables.length >
   * 0`. Latches on (never back off while the id is unchanged) so an
   * empty-layout switch or undo-to-empty pass cannot re-arm the
   * streamed-content fit for a document that already framed.
   */
  contentSeen: boolean;
}

/**
 * Inputs to the per-document auto-fit gate decision (issue #225).
 *
 * Pure-value bundle so the gate predicate can be unit-tested in
 * isolation from the R3F `runSceneBulkLoad` closure (which needs a live
 * THREE scene + WebGL context). See `shouldAutoFitCamera`.
 */
export interface AutoFitDecisionInputs {
  /**
   * `cameraFitDocStateRef.current` — the document claim recorded by the
   * previous bulk-load pass (`null` before the first pass ever). The
   * gate must read the PRE-claim value; `runSceneBulkLoad` advances the
   * ref via `claimAutoFitDocument` only AFTER the gate has decided.
   */
  lastFitDocState: CameraFitDocState | null;
  /**
   * `cb.geometryDocument.id` of the document this bulk-load pass is
   * rendering. `DrawingDocument.id` is a required non-empty string
   * (`drawingDocumentSchema`), distinct per import (a fresh
   * `crypto.randomUUID()` in the OPEN reducer) and stable across edits
   * (the geometry reducers spread the document, preserving `id`).
   */
  currentDocumentId: string;
  /** Number of renderables this pass produced. */
  renderableCount: number;
  /**
   * Whether this pass took the cache-direct lift path (huge / streaming
   * imports — `state.drawing.entities` empty while `entityMetadataIndex`
   * is populated). Two roles in the gate: (a) the unchanged
   * `(useCacheDirect || renderableCount > 1)` live-create sub-gate that
   * protects the draw-line-1-on-empty-stage UX, and (b) the #235
   * follow-up streamed-OPEN clause — only a cache-direct pass may fit a
   * SAME-id document whose claim has not yet seen content, because the
   * cache-direct lift is the import pipeline materialising the streamed
   * file; user-drawn content always arrives through the normal entity
   * feed (non-cache-direct) and must never snap the camera.
   */
  useCacheDirect: boolean;
}

/**
 * Decide whether `runSceneBulkLoad` should auto-fit the camera on this
 * pass (issue #225 — auto-fit once PER DOCUMENT, not once per
 * SceneContent lifetime; #235 follow-up — the document's one fit fires
 * when its content actually becomes renderable, never while drawing).
 *
 * AutoCAD parity: opening a drawing zooms to extents ONCE; adding an
 * entity to the open drawing never auto-zooms (only an explicit ZOOM E).
 * So we fit the first time a given `geometryDocument.id` shows
 * renderable content — edits keep the id, a new import / "new drawing"
 * produces a new id.
 *
 * Trigger (all must hold):
 *   1. `renderableCount > 0` — never fit an empty scene (an empty NEW /
 *      reset drawing has nothing to frame, and a streamed import's
 *      0-renderable pre-pass must not consume the document's one fit).
 *   2. `useCacheDirect || renderableCount > 1` — the unchanged
 *      live-create sub-gate: a single live-drawn entity on the SAME
 *      document must not trip a tight fit (regression fixed in d82e913;
 *      see the gate comment in SceneBulkLoad). This sub-gate is keyed on
 *      renderable count, NOT document id, so it still guards the
 *      live-create path even when the id is unchanged.
 *   3. The per-document clause — either of:
 *      a. `currentDocumentId` differs from the claimed `documentId`
 *         (or nothing was ever claimed) — a NEW document load / re-open
 *         re-fits (issue #225), while same-id edits never do; OR
 *      b. #235 follow-up (@codex P2, streamed OPEN): the id matches but
 *         the claim has `contentSeen: false` AND this pass is
 *         cache-direct — the streamed document was claimed by its
 *         0-renderable pre-pass (the editorStore commits the new
 *         `geometryDocument` before `entityMetadataIndex` is rebuilt)
 *         and its content is only NOW materialising through the
 *         cache-direct lift. The `useCacheDirect` conjunct is what
 *         keeps start-from-empty drawing safe: user-drawn entities
 *         arrive through the normal entity feed, so a same-id
 *         non-cache-direct pass can NEVER fit regardless of how the
 *         empty document was claimed.
 *
 * The pre-fix predicate OR-ed a `!everInitialized` lifetime-latch clause
 * here. That was the second #235 follow-up @codex P2: for a
 * start-from-empty session (no fit has ever run) the empty pass and the
 * first drawn entity both claimed the seed id WITHOUT setting the latch,
 * so the 2nd drawn entity (`renderableCount > 1`, same id) still forced
 * a fit through the OR-branch — the camera snapped mid-drawing, which
 * AutoCAD never does. The clause was also redundant for the legitimate
 * first-ever fit (a never-claimed / different id already satisfies 3a),
 * so it is removed rather than patched.
 *
 * Net behaviour:
 *   - first load of doc A → fits (no claim yet → 3a).
 *   - edit adding a 2nd entity to A → does NOT fit (same id, claim has
 *     `contentSeen: true`; sub-gate #2 also blocks the 1-entity case).
 *   - load of doc B (new id) → fits B (3a).
 *   - streamed load of doc C → pre-pass (0 renderables) does not fit and
 *     claims `{C, contentSeen: false}`; the metadata-index-triggered
 *     cache-direct pass fits via 3b, after which `contentSeen` latches
 *     and later cache-direct re-runs (layer toggles, layout switches)
 *     keep the user's camera untouched.
 *   - reset / NEW then draw 1, 2, … n entities → never fits (same seed
 *     id, non-cache-direct → neither 3a nor 3b).
 *   - switching back to A via a fresh bulk-load (A's id differs from the
 *     claimed B id) → fits A again — AutoCAD-correct (re-open re-zooms).
 */
export function shouldAutoFitCamera(inputs: AutoFitDecisionInputs): boolean {
  const {
    lastFitDocState,
    currentDocumentId,
    renderableCount,
    useCacheDirect,
  } = inputs;
  if (renderableCount <= 0) return false;
  if (!(useCacheDirect || renderableCount > 1)) return false;
  // 3a — per-document gate: a document id that was never claimed (first
  // pass ever) or differs from the claimed one (NEW document loaded in
  // the same session / re-open) gets its fit.
  if (
    lastFitDocState === null ||
    currentDocumentId !== lastFitDocState.documentId
  ) {
    return true;
  }
  // 3b — #235 follow-up (@codex P2, streamed OPEN): same id, but the
  // claim was made by a 0-renderable pre-pass (`contentSeen: false`) and
  // the content is materialising NOW through the cache-direct lift. This
  // is the streamed document's one deferred fit. Non-cache-direct
  // same-id passes (user drawing into an empty/reset document) never
  // reach `true` here.
  return useCacheDirect && !lastFitDocState.contentSeen;
}

/**
 * Claim transition for `cameraFitDocStateRef` — runs on EVERY bulk-load
 * pass, AFTER `shouldAutoFitCamera` has read the pre-claim value (the
 * gate decides on the PREVIOUS pass's claim; only then does the ref
 * advance).
 *
 * Two rules:
 *   - id changed (or first pass ever) → fresh claim; `contentSeen`
 *     starts at whether THIS pass had renderables. A streamed OPEN's
 *     0-renderable pre-pass therefore claims `contentSeen: false`,
 *     leaving the document's one fit armed for the cache-direct content
 *     pass (#235 follow-up @codex P2).
 *   - same id → `contentSeen` latches on (`prev || this-pass`); it never
 *     drops back to `false` while the id is unchanged, so an
 *     empty-layout switch / undo-to-empty pass cannot re-arm the
 *     streamed-content fit for a document that already framed (a
 *     subsequent redo / layer-toggle cache-direct re-run must keep the
 *     user's camera untouched).
 *
 * Returns a fresh object (no in-place mutation) so the caller's ref
 * assignment is the single write point.
 */
export function claimAutoFitDocument(
  prev: CameraFitDocState | null,
  currentDocumentId: string,
  renderableCount: number,
): CameraFitDocState {
  const passHadContent = renderableCount > 0;
  if (prev !== null && prev.documentId === currentDocumentId) {
    return {
      documentId: currentDocumentId,
      contentSeen: prev.contentSeen || passHadContent,
    };
  }
  return { documentId: currentDocumentId, contentSeen: passHadContent };
}
